/* global DOMParser, Event */
import { Controller } from '@hotwired/stimulus'

export default class PaginationController extends Controller {
  static targets = ['perPageSelect']
  static values = {
    locationId: Number,
    currentPage: Number,
    perPage: Number,
    totalPages: Number,
    tabType: String
  }

  static LOADING_OPACITY = '0.6'

  connect () {
  }

  nextPage (event) {
    event.preventDefault()
    if (this.currentPageValue < this.totalPagesValue) {
      this.goToPage(this.currentPageValue + 1)
    }
  }

  previousPage (event) {
    event.preventDefault()
    if (this.currentPageValue > 1) {
      this.goToPage(this.currentPageValue - 1)
    }
  }

  changePerPage (event) {
    const newPerPage = parseInt(event.target.value)
    if (newPerPage !== this.perPageValue) {
      this.perPageValue = newPerPage
      this.goToPage(1)
    }
  }

  goToPage (pageNumber) {
    if (!this.locationIdValue) {
      return
    }

    const url = this.buildUrl(pageNumber)
    this.showLoading()

    this.fetchPage(url)
      .catch(() => {
        this.hideLoading()
      })
  }

  buildUrl (pageNumber) {
    const baseUrl = `/location_management/parking_sessions/filter/${this.locationIdValue}`
    const params = new URLSearchParams({
      page: pageNumber,
      per_page: this.perPageValue,
      tab_type: this.tabTypeValue
    })

    const filterController = this.getFilterController()
    if (filterController) {
      const filters = this.getFiltersFromController(filterController)
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value)
        }
      })
    }

    return `${baseUrl}?${params.toString()}`
  }

  getFilterController () {
    const filtersElement = document.querySelector('[data-controller*="parking-session-filters"]')
    if (!filtersElement) return null

    const application = window.Stimulus || this.application
    return application.getControllerForElementAndIdentifier(
      filtersElement,
      'parking-session-filters'
    )
  }

  getFiltersFromController (filterController) {
    if (!filterController) return {}

    return {
      session_type: filterController.hasSessionTypeTarget ? filterController.sessionTypeTarget.value : '',
      query: filterController.hasQueryTarget ? filterController.queryTarget.value.trim() : ''
    }
  }

  async fetchPage (url) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/vnd.turbo-stream.html, text/html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const contentType = response.headers.get('Content-Type')
    const html = await response.text()

    if (this.isTurboStreamResponse(contentType)) {
      this.processTurboStream(html)
    } else {
      this.updateContent(html)
    }

    this.hideLoading()
  }

  isTurboStreamResponse (contentType) {
    return contentType && contentType.includes('text/vnd.turbo-stream.html')
  }

  processTurboStream (html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const turboStreams = doc.querySelectorAll('turbo-stream')

    turboStreams.forEach(stream => this.applyTurboStreamAction(stream))

    setTimeout(() => {
      window.dispatchEvent(new Event('turbo:load'))
    }, 50)
  }

  applyTurboStreamAction (stream) {
    const action = stream.getAttribute('action')
    const target = stream.getAttribute('target')
    const template = stream.querySelector('template')

    if (!action || !target || !template) return

    const targetElement = document.getElementById(target)
    if (!targetElement) return

    const content = template.innerHTML

    switch (action) {
      case 'replace':
        targetElement.outerHTML = content
        break
      case 'update':
        targetElement.innerHTML = content
        break
      case 'append':
        targetElement.insertAdjacentHTML('beforeend', content)
        break
      case 'prepend':
        targetElement.insertAdjacentHTML('afterbegin', content)
        break
      case 'remove':
        targetElement.remove()
        break
    }
  }

  updateContent (html) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const contentId = `${this.tabTypeValue}-sessions-content`
    const newContent = doc.getElementById(contentId)
    const currentContent = document.getElementById(contentId)

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML
    }
  }

  showLoading () {
    this.setLoadingState(this.constructor.LOADING_OPACITY, 'none')
  }

  hideLoading () {
    this.setLoadingState('1', 'auto')
  }

  setLoadingState (opacity, pointerEvents) {
    const container = document.getElementById('sessions-container')
    if (!container) return

    container.style.opacity = opacity
    container.style.pointerEvents = pointerEvents
  }
}
